import React, { memo, useCallback, useMemo } from 'react';

import { Box, FormControlLabel, Switch } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  appraisalOperationOptions,
  AppraisalOperationType,
  appraisalResubmitModeOptions,
  DEFAULT_APPRAISAL_DUE_DATE_BUSINESS_DAYS,
  FieldTypesEnum,
} from 'constants/loanActionEditor';
import { DataType } from 'constants/taskCategories';
import { deleteByIndex, setIn } from 'utils/data';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { REGGORA_SUPPORTING_DOCUMENTS_DESCRIPTION } from './LoanActionAppraisalServiceRow.constants';
import { excludePurchaseAgreement } from './LoanActionAppraisalServiceRow.utils';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import { getOptionCategoryName } from './LoanActionSelectOption.utils';
import SupportingDocumentsField from './SupportingDocumentsField';

const emptyTaskTypesOptions = [];

const emptyDocumentTypesOptions = [];

const iframeOnlyAppraisalOperations = [
  AppraisalOperationType.OPEN_LOAN_FILE,
  AppraisalOperationType.OPEN_ORDER,
];

const LoanActionAppraisalServiceRow = ({
  index,
  rootPath,
  taskTypesOptions = emptyTaskTypesOptions,
  documentTypesOptions = emptyDocumentTypesOptions,
  isDisabled = false,
  isEditAllowed,
  isAutomatedActions = false,
  isSingleItem,
}) => {
  const { form, setInForm, setForm, setValidationErrors } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const operationFieldPath = `${fieldPath}.operation`;
  const resubmitModeFieldPath = `${fieldPath}.resubmitMode`;
  const dueDateBusinessDaysFieldPath = `${fieldPath}.dueDateBusinessDays`;
  const paymentTaskTypeFieldPath = `${fieldPath}.paymentTaskType`;
  const titleFieldPath = `${fieldPath}.title`;
  const descriptionFieldPath = `${fieldPath}.description`;
  const allowManualSelectionFieldPath = `${fieldPath}.allowManualSelection`;
  const reasonFieldPath = `${fieldPath}.reason`;
  const promptForReasonAtExecutionFieldPath = `${fieldPath}.promptForReasonAtExecution`;
  const promptForEscalationIdAtExecutionFieldPath = `${fieldPath}.promptForEscalationIdAtExecution`;
  const supportingDocumentsFieldPath = `${fieldPath}.supportingDocuments`;

  const areFieldsDisabled = isDisabled || !isEditAllowed;
  const forms = _.get(form, rootPath);
  const operation = _.get(form, operationFieldPath);
  const operationValue = operation?.value;
  const allowManualSelection = _.get(form, allowManualSelectionFieldPath);
  const promptForReasonAtExecution = _.get(form, promptForReasonAtExecutionFieldPath);

  const externalActionTaskTypeOptions = useMemo(
    () => taskTypesOptions.filter(taskType => taskType.dataType === DataType.EXTERNAL_ACTION),
    [taskTypesOptions],
  );

  const supportingDocumentTypesOptions = useMemo(
    () => excludePurchaseAgreement(documentTypesOptions),
    [documentTypesOptions],
  );

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];

    newForms.splice(index, 1);

    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  const onChangeOperation = useCallback(
    (event, option) => {
      setForm(prev =>
        setIn(
          _.omit(prev, [
            resubmitModeFieldPath,
            paymentTaskTypeFieldPath,
            titleFieldPath,
            descriptionFieldPath,
            allowManualSelectionFieldPath,
            reasonFieldPath,
            promptForReasonAtExecutionFieldPath,
            promptForEscalationIdAtExecutionFieldPath,
            supportingDocumentsFieldPath,
          ]),
          dueDateBusinessDaysFieldPath,
          option?.value === AppraisalOperationType.SUBMIT
            ? DEFAULT_APPRAISAL_DUE_DATE_BUSINESS_DAYS
            : null,
        ),
      );
    },
    [
      setForm,
      resubmitModeFieldPath,
      dueDateBusinessDaysFieldPath,
      paymentTaskTypeFieldPath,
      titleFieldPath,
      descriptionFieldPath,
      allowManualSelectionFieldPath,
      reasonFieldPath,
      promptForReasonAtExecutionFieldPath,
      promptForEscalationIdAtExecutionFieldPath,
      supportingDocumentsFieldPath,
    ],
  );

  const onToggleAllowManualSelection = useCallback(
    e => setInForm(allowManualSelectionFieldPath, e.target.checked),
    [setInForm, allowManualSelectionFieldPath],
  );

  const onChangePromptForReasonAtExecution = useCallback(() => {
    setInForm(reasonFieldPath, null);
  }, [setInForm, reasonFieldPath]);

  const isSubmit = operationValue === AppraisalOperationType.SUBMIT;
  const isSendPaymentApp = operationValue === AppraisalOperationType.SEND_PAYMENT_APP;
  const isRequestRevision = operationValue === AppraisalOperationType.REQUEST_REVISION;
  const isAcceptOrDenyFeeEscalation = [
    AppraisalOperationType.ACCEPT_FEE_ESCALATION,
    AppraisalOperationType.DENY_FEE_ESCALATION,
  ].includes(operationValue);
  const areRevisionManualSelectionShown = isRequestRevision && !isAutomatedActions;
  const areRevisionFieldsShown = isRequestRevision && (!allowManualSelection || isAutomatedActions);

  const isHold = operationValue === AppraisalOperationType.HOLD;
  const showHoldPromptCheckbox = isHold && !isAutomatedActions;
  const showPromptForEscalationIdAtExecutionCheckbox =
    isAcceptOrDenyFeeEscalation && !isAutomatedActions;
  const showEscalationAtExecutionBanner = isAcceptOrDenyFeeEscalation && isAutomatedActions;
  const showHoldReasonInput = isHold && (isAutomatedActions || !promptForReasonAtExecution);

  // "Open Loan File" and "Open Order Page" open an in-page iframe drawer for a person, so they are not
  // offered for automated actions, which run headless with no user or UI to receive the link.
  const operationOptions = useMemo(
    () =>
      isAutomatedActions
        ? appraisalOperationOptions.filter(
            ({ value }) => !iframeOnlyAppraisalOperations.includes(value),
          )
        : appraisalOperationOptions,
    [isAutomatedActions],
  );

  return (
    <Box className={wrapperClassname}>
      <Box className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          label="Appraisal Operation"
          name={operationFieldPath}
          options={operationOptions}
          type={FieldTypesEnum.SELECT}
          onChange={onChangeOperation}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </Box>

      {isSubmit && (
        <DynamicValueInput
          required
          disabled={isDisabled || !isEditAllowed}
          label="Resubmit Mode"
          name={resubmitModeFieldPath}
          options={appraisalResubmitModeOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}

      {isSubmit && (
        <DynamicValueInput
          required
          disabled={areFieldsDisabled}
          label="Due Date (Business Days)"
          name={dueDateBusinessDaysFieldPath}
          type={FieldTypesEnum.INTEGER}
        />
      )}

      {isSubmit && (
        <SupportingDocumentsField
          description={REGGORA_SUPPORTING_DOCUMENTS_DESCRIPTION}
          disabled={areFieldsDisabled}
          documentTypesOptions={supportingDocumentTypesOptions}
          fieldPath={supportingDocumentsFieldPath}
        />
      )}

      {showPromptForEscalationIdAtExecutionCheckbox && (
        <DynamicValueInput
          disabled={isDisabled || !isEditAllowed}
          label="Allow manual selection at execution time"
          name={promptForEscalationIdAtExecutionFieldPath}
          type={FieldTypesEnum.BOOLEAN}
        />
      )}

      {showEscalationAtExecutionBanner && (
        <Box>
          <LabelCDS
            size={LabelSize.M}
            title="Auto-actions process all currently-open fee escalations on the loan's appraisal order. No per-execution selection."
            variant={LabelVariant.BRAND}
          />
        </Box>
      )}

      {isSendPaymentApp && (
        <DynamicValueInput
          required
          disabled={isDisabled || !isEditAllowed}
          groupBy={getOptionCategoryName}
          label="Payment Task Type"
          name={paymentTaskTypeFieldPath}
          options={externalActionTaskTypeOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}

      {areRevisionManualSelectionShown && (
        <FormControlLabel
          control={
            <Switch
              checked={!!allowManualSelection}
              disabled={areFieldsDisabled}
              onChange={onToggleAllowManualSelection}
            />
          }
          data-testid={loanManagementTestIds.getAllowManualSelectionSwitchId(fieldPath)}
          label="Allow manual selection at execution time"
        />
      )}

      {areRevisionFieldsShown && (
        <>
          <DynamicValueInput
            required
            disabled={areFieldsDisabled}
            label="Title"
            name={titleFieldPath}
            type={FieldTypesEnum.STRING}
          />
          <DynamicValueInput
            required
            disabled={areFieldsDisabled}
            label="Description"
            name={descriptionFieldPath}
            type={FieldTypesEnum.STRING}
          />
        </>
      )}

      {showHoldPromptCheckbox && (
        <DynamicValueInput
          disabled={isDisabled || !isEditAllowed}
          label="Allow manual selection at execution time"
          name={promptForReasonAtExecutionFieldPath}
          type={FieldTypesEnum.BOOLEAN}
          onChange={onChangePromptForReasonAtExecution}
        />
      )}

      {showHoldReasonInput && (
        <DynamicValueInput
          disabled={isDisabled || !isEditAllowed}
          label="Reason"
          name={reasonFieldPath}
        />
      )}
    </Box>
  );
};

LoanActionAppraisalServiceRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  taskTypesOptions: PropTypes.array,
  documentTypesOptions: PropTypes.array,
  isDisabled: PropTypes.bool,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionAppraisalServiceRow);
